// WP3 — Ortak gelen kutusu (personel). WP4 yanıt döngüsüyle yazma eklendi.
//
// Liste:    GET  /api/v2/inbox/conversations
// Konuşma:  GET  /api/v2/inbox/conversations/:id/messages   (konusma.pencere dahil)
// Yanıt:    POST /api/v2/inbox/conversations/:id/reply      { govde, idempotencyKey }
// Onay:     POST /api/v2/inbox/messages/:id/approve
// Red:      POST /api/v2/inbox/messages/:id/reject          { not? }
// Yönetim:  GET  /api/v2/omni/conversations/:id/yonetim       (WP3 dilim 3: durum, atama, etiket, iç not)
//           POST /api/v2/omni/conversations/:id/{durum,atama,etiketler,notlar}
//
// Arayüz HİÇBİR ŞEY GÖNDERMEZ: uçlar satır yazar, taşıyıcı bütün kapılara (izin, 24 saat penceresi,
// onay) yeniden bakıp gönderir. Yazma alanını pencere kapalıyken kapatmak kolaylıktır; asıl kapı sunucuda.
// Müşteri görüntüleyici (client-os-mesajlar.ts) salt okuma kalır ve ui-readonly-guard kapsamındadır.

import { escHtml, escAttr } from '../utils.js';
import { apiGetJson, apiFetch } from '../api.js';
import {
  yonetimPaneliHtml, atamaRozeti, etiketMetniniAyir, slaRozetleri, hazirCevapSecimiHtml, hazirCevabiEkle,
  type KonusmaYonetimi, type SlaOzeti,
} from '../omnichannel/konusma-yonetimi-gorunum.js';

// escHtml tırnak kaçışlamaz; öznitelik değerine giden her dinamik metin bundan geçer.
function attr(s: unknown): string {
  return escAttr(escHtml(s ?? ''));
}

export interface GelenKutusuKonusma {
  id: string;
  /** Ana firma. */
  tenantId: string;
  /** Marka; eski yanıtta yoksa firma sayılır. */
  marka?: string;
  channel: string;
  externalContactId: string | null;
  kisiEtiketi: string | null;
  status: string;
  lastMessageAt: string | null;
  aiSummary: string | null;
  mesajSayisi: number;
  eskiMesajSayisi: number;
  eskiKayit: boolean;
  sonMesaj: null | { govde: string | null; yon: string | null; zaman: string | null; aiOnaysiz: boolean };
  /** WP3 dilim 3 — eski yanıtta yok. */
  atama?: null | { tur: 'staff' | 'agent'; kimlik: string };
  etiketler?: string[];
  /** WP3 dilim 4 — eski yanıtta yok. */
  sla?: SlaOzeti;
}

export interface GelenKutusuMesaj {
  id: string;
  yon: string;
  yazarTuru: string;
  yazarAjan: string | null;
  govde: string | null;
  zaman: string;
  teslimDurumu: string;
  aiUretimi: boolean;
  onaylayan: string | null;
  aiOnaysiz: boolean;
}

export type PencereBilgisi =
  | { durum: 'pencere_yok' }
  | { durum: 'acik'; kalanDakika: number }
  | { durum: 'kapali'; sablonGerekir: boolean };

/** 24 saat kapandıktan sonra insan temsilci uzatması (Meta HUMAN_AGENT, 7 gün) geçerli mi. */
export type InsanTemsilciBilgisi = { uygun: true; kalanDakika: number } | { uygun: false; sebep?: string };

export interface KonusmaBasligi {
  id: string; tenantId: string; marka?: string; channel: string; externalContactId: string | null; status: string;
  pencere?: PencereBilgisi;
  insanTemsilci?: InsanTemsilciBilgisi;
}

/**
 * Yazma alanı kapalı mı? Meta kanalında 24 saat kapandıysa kapalıdır — İSTİSNA: insan temsilci
 * uzatması açıksa ekip üyesi 7 gün içinde yanıt yazabilir (yanıtı insan yazdığı için Meta izin verir).
 */
export function yazmaKapaliMi(k: KonusmaBasligi): boolean {
  if (!META_KANALLARI.has(k.channel)) return false;
  if (k.pencere?.durum !== 'kapali') return false;
  return k.insanTemsilci?.uygun !== true;
}

export interface GelenKutusuFiltre { channel?: string; status?: string; tenantId?: string; marka?: string; etiket?: string; atama?: string; sla?: string; q?: string }

/** "firma / marka"; marka firmayla aynıysa (ör. castintech) ya da yoksa yalnız firma. */
function firmaMarka(tenantId: string, marka: string | undefined): string {
  return marka && marka !== tenantId ? `${tenantId} / ${marka}` : tenantId;
}

interface ListeYaniti { ok: true; toplam: number; konusmalar: GelenKutusuKonusma[] }
interface KonusmaYaniti { ok: true; konusma: KonusmaBasligi; mesajlar: GelenKutusuMesaj[] }

export type ListeDurumu =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; konusmalar: GelenKutusuKonusma[]; toplam: number }
  | { kind: 'error'; mesaj: string };

export type KonusmaDurumu =
  | { kind: 'bos' }
  | { kind: 'loading'; id: string }
  | { kind: 'ready'; id: string; konusma: KonusmaBasligi; mesajlar: GelenKutusuMesaj[]; bildirim?: string; yonetim?: KonusmaYonetimi | null }
  | { kind: 'error'; id: string; mesaj: string };

const ATAMA_FILTRELERI = ['yok', 'hermes', 'personel'];
const SLA_FILTRELERI = ['bekliyor', 'gecikti', 'pencere'];
const KANALLAR = ['telegram', 'whatsapp', 'instagram_dm', 'instagram_comment', 'facebook_page', 'facebook_comment', 'web_form', 'email', 'manual'];
const DURUMLAR = ['open', 'waiting_customer', 'waiting_agent', 'closed', 'archived'];
const META_KANALLARI = new Set(['facebook_page', 'instagram_dm', 'whatsapp']);
const TESLIM_ETIKET: Record<string, string> = {
  pending: 'bekliyor', sending: 'gönderiliyor', sent: 'kanala iletildi', delivered: 'iletildi', read: 'okundu',
  failed: 'gönderilemedi', cancelled: 'kapatıldı', received: 'alındı',
};

/**
 * Durum etiketi. İki ayrım bilinçli:
 *   · `sent` = "kanala iletildi" — kanalın KABULÜ, müşterinin gördüğü bilgisi DEĞİL (okundu kanıtı yok).
 *   · `cancelled` iki farklı şeyde kullanılıyor: reddedilen AI önerisi ve operatörün "gitmemiş" kararıyla
 *     kapatılan satır. Aynı kelime ikisini karıştırıyordu; bağlama göre yazılır.
 */
function teslimEtiketi(m: GelenKutusuMesaj): string {
  if (m.teslimDurumu === 'cancelled') return m.aiUretimi && !m.onaylayan ? 'reddedildi' : 'kapatıldı (gönderilmedi)';
  return TESLIM_ETIKET[m.teslimDurumu] ?? m.teslimDurumu;
}

/**
 * Gönderim sonucu paneli (operatör kapanışı) — #1119'un eksik parçası.
 *
 * NEDEN: belirsiz sonuç artık OTOMATİK yeniden denenmiyor (mesaj karşıya gitmiş olabilir; Send API'de
 * idempotency anahtarı yok). Bu yüzden kapanış kararı insanda: karşı kanaldan bakıp "gitmiş" ya da
 * "gitmemiş" demek gerekiyor. Terminalden elle POST atmak ürün değildir — karar bu ekranda verilir.
 *
 * Panel dört durumu AYRI gösterir: kanal kabulü (`sent`), gönderilemedi (kesin), sonucu belirsiz
 * (gitmiş olabilir) ve gönderim yarıda (istek gitti, sonucu yazılamadı). Kopya riski taşıyan "yeniden
 * gönder" kararı açık onay kutusu olmadan gönderilemez.
 */
export type SonucVerisi = {
  mesajId: string; durum: string; kanal: string; marka: string;
  sinif: 'gecici' | 'kalici' | 'belirsiz' | 'bilinmiyor'; sebep: string | null;
  kapatilabilir: boolean; kararlar: string[];
};
export type SonucPaneli =
  | { mesajId: string; kind: 'loading' }
  | { mesajId: string; kind: 'ready'; veri: SonucVerisi; uyari?: string; kopyaOnayi: boolean }
  | { mesajId: string; kind: 'error'; mesaj: string };

let sonucPaneli: SonucPaneli | null = null;

const SINIF_ETIKET: Record<string, { metin: string; renk: string; aciklama: string }> = {
  belirsiz: {
    metin: 'sonuç BELİRSİZ', renk: '#8a5a00',
    aciklama: 'İstek karşı tarafa ULAŞMIŞ olabilir; yanıtı alamadık. Otomatik yeniden denenmedi — '
      + 'aynı mesajı ikinci kez göndermemek için karşı kanaldan (Messenger/Instagram/Telegram) kontrol edin.',
  },
  kalici: {
    metin: 'gönderilemedi (kesin)', renk: '#8f2d2d',
    aciklama: 'Karşı taraf isteği REDDETTİ (pencere kapalı, izin yok, metin uzun vb.). Mesaj gitmedi; '
      + 'yeniden denemek durumu değiştirmez.',
  },
  gecici: {
    metin: 'geçici hata', renk: '#5a5a8a',
    aciklama: 'Sağlayıcı kesin bir hata döndürdü (oran sınırı / sunucu hatası). Mesaj gitmedi; nöbet '
      + 'kendiliğinden yeniden deniyor.',
  },
  bilinmiyor: {
    metin: 'sınıflandırılamadı', renk: '#4a4a4a',
    aciklama: 'Hata metni bilinen sınıflara girmiyor. Bir kez yeniden denenir; sonucu karşı kanaldan doğrulayın.',
  },
};

const KARAR_ETIKET: Record<string, { baslik: string; aciklama: string }> = {
  'teslim-edildi': {
    baslik: 'Karşı kanalda GÖRDÜM — teslim edildi',
    aciklama: 'Mesajın karşı tarafta göründüğünü doğruladınız. Satır "iletildi" olarak kapanır; kararınız '
      + 'kim/ne zaman/hangi notla verildiği bilgisiyle kaydedilir. (Bu bir okundu kanıtı DEĞİLDİR.)',
  },
  gonderilmedi: {
    baslik: 'Karşı kanalda YOK — gitmemiş',
    aciklama: 'Mesajın gitmediğini doğruladınız. Satır kapatılır; müşteriye yeniden yazmak ayrı bir karardır.',
  },
  'yeniden-gonder': {
    baslik: 'Yeniden gönder (KOPYA RİSKİ)',
    aciklama: 'Mesaj karşı tarafa gitmiş olabilir. Yeniden göndermek müşteriye AYNI yanıtın ikinci kez '
      + 'ulaşması anlamına gelebilir. Bu kararı ancak karşı kanalda mesajın OLMADIĞINDAN emin değilken ve '
      + 'riski kabul ederek verin.',
  },
};

let liste: ListeDurumu = { kind: 'idle' };
let secili: KonusmaDurumu = { kind: 'bos' };
let filtre: GelenKutusuFiltre = {};
let mesgul = false;
let yanitAnahtari = '';

export async function loadGelenKutusu(): Promise<void> {
  liste = { kind: 'loading' };
  const r = await apiGetJson<ListeYaniti>('/api/v2/inbox/conversations', { ...filtre, limit: 100 });
  liste = r.ok
    ? { kind: 'ready', konusmalar: r.data.konusmalar, toplam: r.data.toplam }
    : { kind: 'error', mesaj: r.message ?? r.error };
}

async function loadKonusma(id: string, bildirim?: string): Promise<void> {
  if (secili.kind !== 'ready' || secili.id !== id) { secili = { kind: 'loading', id }; renderGelenKutusu(); }
  const [r, y] = await Promise.all([
    apiGetJson<KonusmaYaniti>(`/api/v2/inbox/conversations/${encodeURIComponent(id)}/messages`),
    apiGetJson<KonusmaYonetimi & { ok: true }>(`/api/v2/omni/conversations/${encodeURIComponent(id)}/yonetim`),
  ]);
  // Yanıt beklenirken seçim değişmiş ya da temizlenmiş olabilir (modül durumu await sırasında değişir;
  // tip daraltması bunu görmez) — bu durumda eski yanıtı yazma.
  const guncel = secili as KonusmaDurumu;
  if (guncel.kind === 'bos' || guncel.id !== id) return;
  secili = r.ok
    ? { kind: 'ready', id, konusma: r.data.konusma, mesajlar: r.data.mesajlar, bildirim, yonetim: y.ok ? y.data : null }
    : { kind: 'error', id, mesaj: r.message ?? r.error };
  renderGelenKutusu();
}

// ── WP3 dilim 5 — canlı yenileme ─────────────────────────────────────────────────────────────────────────────
// SSE olayı (omni.message.new / omni.conversation.updated) → liste ve açık konuşma sessizce yenilenir. Render
// innerHTML'i baştan kurduğu için personelin yazmakta olduğu yanıt/not/etiket metni kaybolurdu: yalnız KULLANICININ
// değiştirdiği alanlar (value ≠ defaultValue) yenilemeden önce alınır, sonra geri konur; odak ve imleç de korunur.

interface AlanBenzeri {
  id: string;
  value: string;
  defaultValue: string;
  disabled?: boolean;
  selectionStart?: number | null;
  selectionEnd?: number | null;
  focus?: () => void;
  setSelectionRange?: (bas: number, son: number) => void;
}
interface KokBenzeri { querySelectorAll(secici: string): ArrayLike<AlanBenzeri> }

export interface Taslak {
  alanlar: Array<{ id: string; deger: string }>;
  odak: null | { id: string; bas: number | null; son: number | null };
}

const TASLAK_SECICI = 'textarea[id], input[id]';

export function taslakAl(kok: KokBenzeri, aktif: { id?: string } | null): Taslak {
  const alanlar: Taslak['alanlar'] = [];
  let odak: Taslak['odak'] = null;
  for (const el of Array.from(kok.querySelectorAll(TASLAK_SECICI))) {
    if (!el.id) continue;
    if (el.value !== el.defaultValue) alanlar.push({ id: el.id, deger: el.value });
    if (aktif && aktif.id && aktif.id === el.id) odak = { id: el.id, bas: el.selectionStart ?? null, son: el.selectionEnd ?? null };
  }
  return { alanlar, odak };
}

export function taslakGeriYukle(kok: KokBenzeri, t: Taslak): void {
  const harita = new Map<string, AlanBenzeri>();
  for (const el of Array.from(kok.querySelectorAll(TASLAK_SECICI))) if (el.id) harita.set(el.id, el);
  for (const a of t.alanlar) {
    const el = harita.get(a.id);
    if (el && !el.disabled) el.value = a.deger;
  }
  if (t.odak) {
    const el = harita.get(t.odak.id);
    if (el && !el.disabled) {
      el.focus?.();
      if (t.odak.bas !== null && t.odak.son !== null) {
        try { el.setSelectionRange?.(t.odak.bas, t.odak.son); } catch { /* seçim desteklenmeyen alan */ }
      }
    }
  }
}

let canliZamanlayici: ReturnType<typeof setTimeout> | null = null;
let canliEtkilenen = new Set<string>();
const CANLI_BEKLEME_MS = 800;

/** SSE olayı geldi: kısa bir bekleme ile toplanır (art arda olaylar tek yenileme olur). */
export function gelenKutusuCanliOlay(olay: { konusmaId?: unknown }): void {
  canliEtkilenen.add(typeof olay?.konusmaId === 'string' ? olay.konusmaId : '*');
  if (canliZamanlayici) return;
  canliZamanlayici = setTimeout(() => { canliZamanlayici = null; void canliYenile(); }, CANLI_BEKLEME_MS);
}

async function canliYenile(): Promise<void> {
  if (mesgul) {
    // Personelin kendi işlemi sürerken yenileme yapılmaz; biraz sonra tekrar denenir.
    canliZamanlayici = setTimeout(() => { canliZamanlayici = null; void canliYenile(); }, CANLI_BEKLEME_MS);
    return;
  }
  const etkilenen = canliEtkilenen;
  canliEtkilenen = new Set();
  const once = document.getElementById('view-gelen-kutusu');
  if (!once) return;
  const taslak = taslakAl(once as unknown as KokBenzeri, document.activeElement as { id?: string } | null);
  await loadGelenKutusu();
  const s = secili;
  if (s.kind === 'ready' && (etkilenen.has(s.id) || etkilenen.has('*'))) await loadKonusma(s.id);
  else renderGelenKutusu();
  const sonra = document.getElementById('view-gelen-kutusu');
  if (sonra) taslakGeriYukle(sonra as unknown as KokBenzeri, taslak);
}

async function postJson(url: string, govde: unknown): Promise<{ ok: boolean; mesaj: string }> {
  try {
    const res = await apiFetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(govde ?? {}) });
    const j = await res.json().catch(() => ({})) as { ok?: boolean; message?: string; error?: string };
    return { ok: res.ok && j.ok === true, mesaj: j.message || j.error || `HTTP ${res.status}` };
  } catch (e) {
    return { ok: false, mesaj: (e as Error).message || 'bağlantı hatası' };
  }
}

export function renderGelenKutusu(): void {
  const root = document.getElementById('view-gelen-kutusu');
  if (!root) return;
  root.innerHTML = renderGelenKutusuHtml(liste, secili, filtre);
  bagla(root);
}

function zamanYaz(z: string | null | undefined): string {
  if (!z) return '—';
  return String(z).replace('T', ' ').slice(0, 16);
}

function rozet(metin: string, renk: string): string {
  return `<span style="display:inline-block;font-size:10px;padding:1px 6px;border-radius:8px;background:${renk};color:#fff;margin-left:4px;">${escHtml(metin)}</span>`;
}

function secim(id: string, etiket: string, deger: string | undefined, secenekler: string[]): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(etiket)}
    <select id="gk-f-${id}" class="form-select" style="width:150px;padding:4px 8px;">
      <option value="">(hepsi)</option>
      ${secenekler.map((o) => `<option value="${attr(o)}"${o === deger ? ' selected' : ''}>${escHtml(o)}</option>`).join('')}
    </select></label>`;
}

function girdi(id: string, etiket: string, deger: string | undefined, yerTutucu: string): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(etiket)}
    <input id="gk-f-${id}" class="form-input" value="${attr(deger)}" placeholder="${attr(yerTutucu)}" style="width:170px;padding:4px 8px;" /></label>`;
}

function konusmaSatiri(k: GelenKutusuKonusma, seciliId: string | null): string {
  const kim = k.kisiEtiketi || k.externalContactId || '(bilinmeyen kişi)';
  const yonOku = k.sonMesaj?.yon === 'outbound' ? '→ ' : k.sonMesaj?.yon === 'inbound' ? '← ' : '';
  const onizleme = k.sonMesaj
    ? `${yonOku}${k.sonMesaj.govde ?? '(boş mesaj)'}`
    : k.eskiKayit ? `Eski kayıtta ${k.eskiMesajSayisi} mesaj` : 'Mesaj yok';
  const aktif = seciliId === k.id;
  return `<button type="button" class="gk-konusma" data-action="gk-ac" data-konusma-id="${attr(k.id)}" aria-pressed="${aktif ? 'true' : 'false'}"
      style="display:block;width:100%;text-align:left;padding:10px 12px;border:0;border-bottom:1px solid var(--border,#2a2a2a);background:${aktif ? 'var(--bg2)' : 'transparent'};color:inherit;cursor:pointer;">
      <div style="display:flex;justify-content:space-between;gap:8px;align-items:baseline;">
        <strong style="font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(kim)}</strong>
        <span style="font-size:11px;color:var(--muted);white-space:nowrap;">${escHtml(zamanYaz(k.sonMesaj?.zaman ?? k.lastMessageAt))}</span>
      </div>
      <div style="font-size:11px;color:var(--muted);margin:2px 0;">
        <code style="background:var(--bg2);padding:1px 5px;border-radius:4px;">${escHtml(k.channel)}</code>
        · ${escHtml(firmaMarka(k.tenantId, k.marka))} · ${escHtml(k.status)}
        ${k.eskiKayit ? rozet('taşınmamış eski kayıt', '#8a6d1f') : ''}
        ${k.sonMesaj?.aiOnaysiz ? rozet('onay bekleyen AI önerisi', '#6b4fa0') : ''}
        ${k.atama ? rozet(atamaRozeti(k.atama), '#2f5d8a') : ''}
        ${slaRozetleri(k.sla).map(([metin, renk]) => rozet(metin, renk)).join('')}
        ${(k.etiketler ?? []).map((e) => rozet(`#${e}`, '#4a5568')).join('')}
      </div>
      <div style="font-size:12px;color:var(--muted);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHtml(onizleme)}</div>
    </button>`;
}

function yazarEtiketi(m: GelenKutusuMesaj): string {
  if (m.yazarTuru === 'staff') return `ekip · ${m.onaylayan ?? ''}`.replace(/ · $/, '');
  return m.yazarAjan ? `${m.yazarTuru} · ${m.yazarAjan}` : m.yazarTuru;
}

/** Operatör kararı bekleyen giden satır mı? `failed` kesin de belirsiz de olabilir; `sending` sonucu yazılmamış. */
function sonucBekliyorMu(m: GelenKutusuMesaj): boolean {
  return m.yon === 'outbound' && (m.teslimDurumu === 'failed' || m.teslimDurumu === 'sending');
}

export function sonucPaneliHtml(m: GelenKutusuMesaj, panel: SonucPaneli | null = sonucPaneli): string {
  const p = panel;
  if (!p || p.mesajId !== m.id) return '';
  const cerceve = (icerik: string): string =>
    `<div class="gk-sonuc" style="margin-top:8px;padding:8px;border:1px solid var(--border,#3a3a3a);border-radius:8px;background:var(--bg1,#111);text-align:left;">
       <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px;">
         <strong style="font-size:12px;">Gönderim sonucu</strong>
         <button type="button" class="btn-secondary" data-action="gk-sonuc-kapat" style="padding:1px 8px;font-size:11px;">Kapat</button>
       </div>${icerik}</div>`;
  if (p.kind === 'loading') return cerceve('<div style="font-size:12px;color:var(--muted);">Durum okunuyor…</div>');
  if (p.kind === 'error') {
    return cerceve(`<div style="font-size:12px;color:#e08080;">Durum okunamadı: ${escHtml(p.mesaj)}</div>`);
  }
  const v = p.veri;
  const s = SINIF_ETIKET[v.sinif] ?? SINIF_ETIKET.bilinmiyor;
  const yarida = v.durum === 'sending';
  const bas = yarida
    ? { metin: 'gönderim YARIDA', renk: '#8a5a00', aciklama: 'İstek gönderildi, sonucu kaydedilemedi. Mesaj karşı tarafa ULAŞMIŞ olabilir — elle doğrulayın, yeniden göndermeyin.' }
    : s;
  if (!v.kapatilabilir) {
    return cerceve(`<div style="font-size:12px;">Bu satır artık kapanmış (<code>${escHtml(v.durum)}</code>); karar gerekmiyor.</div>`);
  }
  const not = `<label style="display:block;font-size:11px;color:var(--muted);margin:6px 0 2px;">İnceleme notu (isteğe bağlı — kanıtı yazın: nerede, ne gördünüz)
      <input id="gk-sonuc-not" class="form-input" maxlength="300" placeholder="ör. Messenger'da 14:05'te göründü" style="width:100%;padding:4px 8px;font-size:12px;" /></label>`;
  const kopya = `<label style="display:flex;gap:6px;align-items:flex-start;font-size:11px;margin:6px 0;color:#d9b24c;">
      <input type="checkbox" id="gk-sonuc-kopya" ${p.kopyaOnayi ? 'checked' : ''} />
      <span>Kopya riskini kabul ediyorum: bu mesaj karşı tarafa gitmiş olabilir ve yeniden gönderim müşteriye
      AYNI yanıtın ikinci kez ulaşmasına yol açabilir.</span></label>`;
  const dugme = (karar: string): string => {
    const k = KARAR_ETIKET[karar];
    if (!k) return '';
    const riskli = karar === 'yeniden-gonder';
    return `<div style="margin:6px 0;">
        <button type="button" class="${riskli ? 'btn-secondary' : 'btn-primary'}" data-action="gk-sonuc-ver"
          data-karar="${attr(karar)}" data-mesaj-id="${attr(v.mesajId)}" style="padding:3px 10px;font-size:12px;">${escHtml(k.baslik)}</button>
        <div style="font-size:11px;color:var(--muted);margin-top:2px;">${escHtml(k.aciklama)}</div>
      </div>`;
  };
  return cerceve(`
    <div style="font-size:12px;margin-bottom:4px;">${rozet(bas.metin, bas.renk)}
      <span style="color:var(--muted);"> ${escHtml(v.kanal)} · ${escHtml(v.marka)}</span></div>
    <div style="font-size:11px;color:var(--muted);margin-bottom:4px;">${escHtml(bas.aciklama)}</div>
    ${v.sebep ? `<div style="font-size:11px;color:var(--muted);margin-bottom:4px;">Sebep: <code>${escHtml(v.sebep.slice(0, 160))}</code></div>` : ''}
    ${p.uyari ? `<div style="font-size:12px;color:#e0a060;margin:4px 0;">${escHtml(p.uyari)}</div>` : ''}
    ${not}
    ${v.kararlar.includes('yeniden-gonder') ? kopya : ''}
    ${v.kararlar.map(dugme).join('')}
    <div style="font-size:11px;color:var(--muted);margin-top:4px;">Emin değilsiniz: panel kapatılabilir, satır
      incelemede kalır ve nöbet 24 saatte bir hatırlatır. Sahte kesinlik üretmek zorunda değilsiniz.</div>`);
}

function mesajBalonu(m: GelenKutusuMesaj, pencereAcik: boolean): string {
  const giden = m.yon === 'outbound';
  const onayBekliyor = giden && m.aiOnaysiz && m.teslimDurumu === 'pending';
  const eylemler = onayBekliyor
    ? `<div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap;">
        <button type="button" class="btn-primary" data-action="gk-onayla" data-mesaj-id="${attr(m.id)}" ${pencereAcik ? '' : 'disabled'} style="padding:3px 10px;font-size:12px;">Onayla ve gönder</button>
        <button type="button" class="btn-secondary" data-action="gk-reddet" data-mesaj-id="${attr(m.id)}" style="padding:3px 10px;font-size:12px;">Reddet</button>
       </div>`
    : sonucBekliyorMu(m)
      ? `<div style="display:flex;gap:6px;margin-top:6px;flex-wrap:wrap;">
        <button type="button" class="btn-secondary" data-action="gk-sonuc" data-mesaj-id="${attr(m.id)}" style="padding:3px 10px;font-size:12px;">Gönderim sonucu…</button>
       </div>${sonucPaneliHtml(m)}`
      : '';
  return `<div class="gk-mesaj gk-${giden ? 'giden' : 'gelen'}" style="display:flex;justify-content:${giden ? 'flex-end' : 'flex-start'};margin:6px 0;">
      <div style="max-width:78%;padding:8px 10px;border-radius:10px;background:${giden ? 'var(--accent-bg,#1f3a5f)' : 'var(--bg2)'};${onayBekliyor ? 'border:1px dashed #6b4fa0;' : ''}">
        <div style="font-size:10px;color:var(--muted);margin-bottom:3px;">
          ${escHtml(yazarEtiketi(m))} · ${escHtml(zamanYaz(m.zaman))} · ${escHtml(teslimEtiketi(m))}
          ${onayBekliyor ? rozet('onay bekleyen AI önerisi — henüz gönderilmedi', '#6b4fa0') : ''}
          ${m.yon === 'outbound' && m.teslimDurumu === 'sending' ? rozet('sonuç yazılmadı — elle doğrulayın', '#8a5a00') : ''}
          ${m.yon === 'outbound' && m.teslimDurumu === 'failed' ? rozet('karar bekliyor', '#8f2d2d') : ''}
        </div>
        <div style="font-size:13px;white-space:pre-wrap;word-break:break-word;">${escHtml(m.govde ?? '(boş mesaj)')}</div>
        ${eylemler}
      </div>
    </div>`;
}

function pencereSatiri(kanal: string, p: PencereBilgisi | undefined, ha?: InsanTemsilciBilgisi): string {
  if (!p || p.durum === 'pencere_yok') return '';
  if (p.durum === 'acik') {
    const saat = Math.floor(p.kalanDakika / 60);
    const dk = p.kalanDakika % 60;
    return `<div class="gk-pencere" style="font-size:11px;color:var(--muted);margin:4px 0;">Yanıt penceresi açık — kalan ${saat} sa ${dk} dk</div>`;
  }
  if (ha?.uygun) {
    // Ekip üyesi yazarsa gider; öneri otomatik gönderilmez (Meta: etiket yalnız insan yanıtında).
    const gun = Math.floor(ha.kalanDakika / 1440);
    const saat = Math.floor((ha.kalanDakika % 1440) / 60);
    return `<div class="gk-pencere gk-pencere-insan" style="font-size:12px;margin:6px 0;padding:6px 8px;border-radius:6px;background:rgba(229,170,60,.14);">${escHtml(`24 saatlik pencere kapalı — ekip yanıtı olarak gönderilebilir (kalan ${gun} gün ${saat} sa). Yanıtı ekip üyesi yazar.`)}</div>`;
  }
  const ek = p.sablonGerekir ? ' Bu kanalda yalnız onaylı şablon gönderilebilir.' : ' Müşteri yeniden yazana kadar mesaj gönderilemez.';
  return `<div class="gk-pencere gk-pencere-kapali" style="font-size:12px;margin:6px 0;padding:6px 8px;border-radius:6px;background:rgba(229,85,85,.12);">${escHtml(`24 saatlik yanıt penceresi kapalı (${kanal}).${ek}`)}</div>`;
}

function yanitAlani(k: KonusmaBasligi, y?: KonusmaYonetimi | null): string {
  const kapali = yazmaKapaliMi(k);
  const sinir = META_KANALLARI.has(k.channel) ? 1000 : 4000;
  return `<div class="gk-yanit" style="margin-top:10px;border-top:1px solid var(--border,#2a2a2a);padding-top:8px;">
      <label for="gk-yanit-metin" style="font-size:11px;color:var(--muted);">Ekip yanıtı</label>
      <textarea id="gk-yanit-metin" class="form-input" rows="3" maxlength="${sinir}" ${kapali ? 'disabled' : ''}
        placeholder="${attr(kapali ? 'Pencere kapalı — yanıt yazılamaz' : 'Yanıtınızı yazın; gönderim izni ve pencere sunucuda yeniden denetlenir')}"
        style="width:100%;box-sizing:border-box;margin-top:4px;"></textarea>
      ${hazirCevapSecimiHtml(y?.hazirCevaplar, kapali)}
      <div style="display:flex;justify-content:space-between;align-items:center;margin-top:6px;gap:8px;">
        <span id="gk-yanit-durum" style="font-size:11px;color:var(--muted);"></span>
        <button type="button" class="btn-primary" data-action="gk-gonder" ${kapali ? 'disabled' : ''} style="padding:4px 12px;">Yanıtı gönder</button>
      </div>
    </div>`;
}

function konusmaPaneli(k: KonusmaDurumu, listedeki: GelenKutusuKonusma | undefined): string {
  if (k.kind === 'bos') {
    return '<div class="empty-state"><div class="empty-icon">💬</div><div class="empty-title">Soldan bir konuşma seç</div></div>';
  }
  if (k.kind === 'loading') {
    return '<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Konuşma yükleniyor…</div></div>';
  }
  if (k.kind === 'error') {
    return `<div class="rt-card-title">Konuşma yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(k.mesaj)}</div>`;
  }
  const kim = listedeki?.kisiEtiketi || k.konusma.externalContactId || '(bilinmeyen kişi)';
  // Öneriyi ONAYLAMAK da insan eylemi; insan temsilci uzatmasında onay düğmesi açık kalır.
  const pencereAcik = !yazmaKapaliMi(k.konusma);
  const baslik = `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px;border-bottom:1px solid var(--border,#2a2a2a);padding-bottom:8px;margin-bottom:6px;">
      <div><strong>${escHtml(kim)}</strong>
        <div style="font-size:11px;color:var(--muted);"><code>${escHtml(k.konusma.channel)}</code> · ${escHtml(firmaMarka(k.konusma.tenantId, k.konusma.marka))} ·${escHtml(k.konusma.status)}</div>
      </div>
      <div style="font-size:11px;color:var(--muted);">${k.mesajlar.length} mesaj</div>
    </div>${pencereSatiri(k.konusma.channel, k.konusma.pencere, k.konusma.insanTemsilci)}${k.yonetim === undefined ? '' : yonetimPaneliHtml(k.yonetim)}`;
  const bildirim = k.bildirim ? `<div class="gk-bildirim" style="font-size:12px;margin:6px 0;">${escHtml(k.bildirim)}</div>` : '';
  if (k.mesajlar.length === 0) {
    const not = listedeki?.eskiKayit
      ? `Bu konuşmanın ${listedeki.eskiMesajSayisi} mesajı eski kayıt biçiminde (JSONB) duruyor ve yeni tabloya taşınmadı.`
      : 'Bu konuşmada kayıtlı mesaj yok.';
    return `${baslik}${bildirim}<div class="empty-state"><div class="empty-icon">📭</div><div class="empty-title">${escHtml(not)}</div></div>`;
  }
  return `${baslik}${bildirim}<div class="gk-akis" style="max-height:55vh;overflow-y:auto;padding-right:4px;">${k.mesajlar.map((m) => mesajBalonu(m, pencereAcik)).join('')}</div>${yanitAlani(k.konusma, k.yonetim)}`;
}

export function renderGelenKutusuHtml(l: ListeDurumu, k: KonusmaDurumu, f: GelenKutusuFiltre = {}): string {
  const baslik = `
    <div class="view-header">
      <div>
        <div class="view-title">Gelen Kutusu</div>
        <div class="view-sub">Bütün kanallardan gelen müşteri konuşmaları. Yanıtlar ve onaylanan AI önerileri, gönderim izni ve 24 saatlik pencere sunucuda denetlendikten sonra gönderilir.</div>
      </div>
    </div>
    <div class="rt-card" style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
      ${secim('channel', 'Kanal', f.channel, KANALLAR)}
      ${secim('status', 'Durum', f.status, DURUMLAR)}
      ${girdi('tenantId', 'Firma', f.tenantId, 'ör. azgen')}
      ${girdi('marka', 'Marka', f.marka, 'ör. mervaks')}
      ${girdi('etiket', 'Etiket', f.etiket, 'ör. teklif')}
      ${secim('atama', 'Atama', f.atama, ATAMA_FILTRELERI)}
      ${secim('sla', 'SLA', f.sla, SLA_FILTRELERI)}
      ${girdi('q', 'Ara', f.q, 'kişi, kimlik ya da mesaj')}
      <button id="gk-uygula" type="button" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
    </div>`;

  if (l.kind === 'idle' || l.kind === 'loading') {
    return `${baslik}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  }
  if (l.kind === 'error') {
    return `${baslik}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Gelen kutusu yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(l.mesaj)}</div></div>`;
  }

  const seciliId = k.kind === 'bos' ? null : k.id;
  const listedeki = seciliId ? l.konusmalar.find((c) => c.id === seciliId) : undefined;
  const sol = l.konusmalar.length === 0
    ? '<div class="empty-state"><div class="empty-icon">📭</div><div class="empty-title">Eşleşen konuşma yok</div></div>'
    : l.konusmalar.map((c) => konusmaSatiri(c, seciliId)).join('');

  return `${baslik}
    <div class="gk-izgara" style="display:grid;grid-template-columns:minmax(260px,380px) 1fr;gap:12px;align-items:start;">
      <div class="rt-card" style="padding:0;overflow:hidden;">
        <div class="rt-card-title" style="padding:10px 12px;">Konuşmalar (${l.konusmalar.length}${l.toplam > l.konusmalar.length ? ` / ${l.toplam}` : ''})</div>
        <div class="gk-liste" style="max-height:70vh;overflow-y:auto;">${sol}</div>
      </div>
      <div class="rt-card">${konusmaPaneli(k, listedeki)}</div>
    </div>`;
}

async function yanitGonder(): Promise<void> {
  if (mesgul || secili.kind !== 'ready') return;
  const alan = document.getElementById('gk-yanit-metin') as HTMLTextAreaElement | null;
  const durum = document.getElementById('gk-yanit-durum');
  const govde = (alan?.value || '').trim();
  if (!govde) { if (durum) durum.textContent = 'Yanıt boş.'; return; }
  // Aynı metnin tekrar tıklanması ikinci satır üretmesin: anahtar başarıya kadar korunur.
  if (!yanitAnahtari) yanitAnahtari = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  mesgul = true;
  if (durum) durum.textContent = 'Kaydediliyor…';
  const id = secili.id;
  const r = await postJson(`/api/v2/inbox/conversations/${encodeURIComponent(id)}/reply`, { govde, idempotencyKey: yanitAnahtari });
  mesgul = false;
  if (!r.ok) { if (durum) durum.textContent = `Gönderilemedi: ${r.mesaj}`; return; }
  yanitAnahtari = '';
  await loadKonusma(id, 'Yanıt kuyruğa alındı; taşıyıcı kısa süre içinde gönderecek.');
}

async function yonetimIslem(eylem: string, hedef: HTMLElement): Promise<void> {
  if (mesgul || secili.kind !== 'ready') return;
  const id = secili.id;
  const deger = (secici: string) => (document.getElementById(secici) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value ?? '';
  let yol = '';
  let govde: Record<string, unknown> = {};
  let basari = '';
  if (eylem === 'gk-durum') { yol = 'durum'; govde = { durum: deger('gk-durum-secim') }; basari = 'Durum kaydedildi.'; }
  if (eylem === 'gk-ata') { yol = 'atama'; govde = { hedef: hedef.dataset.hedef }; basari = hedef.dataset.hedef === 'kaldir' ? 'Atama kaldırıldı.' : 'Atandı.'; }
  if (eylem === 'gk-etiket-kaydet') { yol = 'etiketler'; govde = { etiketler: etiketMetniniAyir(deger('gk-etiketler')) }; basari = 'Etiketler kaydedildi.'; }
  if (eylem === 'gk-not-ekle') {
    const metin = deger('gk-not-metin').trim();
    if (!metin) return;
    yol = 'notlar'; govde = { govde: metin }; basari = 'İç not eklendi.';
  }
  // Hazır cevaplar (WP3 dilim 4): firma + marka sunucuda konuşmadan alınır.
  let tamYol = '';
  const hid = hedef.dataset.hazirId ?? '';
  if (eylem === 'gk-hazir-ekle') {
    tamYol = `/api/v2/omni/conversations/${encodeURIComponent(id)}/hazir-cevaplar`;
    govde = { baslik: deger('gk-hazir-baslik'), metin: deger('gk-hazir-metin') }; basari = 'Hazır cevap eklendi.';
  }
  if (eylem === 'gk-hazir-guncelle' && hid) {
    tamYol = `/api/v2/omni/hazir-cevaplar/${encodeURIComponent(hid)}`;
    govde = { baslik: deger(`gk-hazir-baslik-${hid}`), metin: deger(`gk-hazir-metin-${hid}`) }; basari = 'Hazır cevap kaydedildi.';
  }
  if (eylem === 'gk-hazir-arsivle' && hid) {
    if (!window.confirm('Bu hazır cevap arşivlensin mi? Listeden kalkar.')) return;
    tamYol = `/api/v2/omni/hazir-cevaplar/${encodeURIComponent(hid)}/arsivle`; basari = 'Hazır cevap arşivlendi.';
  }
  if (!yol && !tamYol) return;
  mesgul = true;
  const r = await postJson(tamYol || `/api/v2/omni/conversations/${encodeURIComponent(id)}/${yol}`, govde);
  mesgul = false;
  if (r.ok && !tamYol) await loadGelenKutusu();
  await loadKonusma(id, r.ok ? basari : `İşlem yapılamadı: ${r.mesaj}`);
}

/** Seçilen hazır cevabı yanıt kutusuna yerleştirir. Ağ isteği YOK; gönderim yine "Yanıtı gönder" + sunucu kapıları. */
function hazirCevabiKullan(): void {
  if (secili.kind !== 'ready') return;
  const secim = document.getElementById('gk-hazir-sec') as HTMLSelectElement | null;
  const alan = document.getElementById('gk-yanit-metin') as HTMLTextAreaElement | null;
  const hc = secili.yonetim?.hazirCevaplar?.find((c) => c.id === secim?.value);
  if (!hc || !alan || alan.disabled) return;
  alan.value = hazirCevabiEkle(alan.value, hc.dolu).slice(0, alan.maxLength > 0 ? alan.maxLength : undefined);
  alan.focus();
}

async function oneriIslem(mesajId: string, islem: 'approve' | 'reject'): Promise<void> {
  if (mesgul || secili.kind !== 'ready') return;
  if (islem === 'reject' && !window.confirm('Bu AI önerisi reddedilsin mi? Reddedilen öneri gönderilmez.')) return;
  mesgul = true;
  const id = secili.id;
  const r = await postJson(`/api/v2/inbox/messages/${encodeURIComponent(mesajId)}/${islem}`, {});
  mesgul = false;
  await loadKonusma(id, r.ok
    ? (islem === 'approve' ? 'Öneri onaylandı; taşıyıcı kısa süre içinde gönderecek.' : 'Öneri reddedildi.')
    : `İşlem yapılamadı: ${r.mesaj}`);
}

/** Panel açılır ve satırın GÜNCEL durumu sunucudan okunur — ekranın bayat hâli karar üretmesin. */
async function sonucAc(mesajId: string): Promise<void> {
  sonucPaneli = { mesajId, kind: 'loading' };
  renderGelenKutusu();
  try {
    const res = await apiFetch(`/api/v2/omni/giden/${encodeURIComponent(mesajId)}`);
    const j = await res.json().catch(() => ({})) as { ok?: boolean; error?: string; message?: string } & Partial<SonucVerisi>;
    if (!res.ok || j.ok !== true) {
      sonucPaneli = { mesajId, kind: 'error', mesaj: j.message || j.error || `HTTP ${res.status}` };
    } else {
      sonucPaneli = { mesajId, kind: 'ready', kopyaOnayi: false, veri: j as unknown as SonucVerisi };
    }
  } catch (e) {
    sonucPaneli = { mesajId, kind: 'error', mesaj: (e as Error).message || 'bağlantı hatası' };
  }
  renderGelenKutusu();
}

async function sonucVer(mesajId: string, karar: string): Promise<void> {
  // Tek uçuş kuralı: işlem sürerken ikinci tıklama aynı mesajı tekrar göndermeye çalışamaz.
  if (mesgul || secili.kind !== 'ready') return;
  const p = sonucPaneli;
  if (!p || p.kind !== 'ready' || p.mesajId !== mesajId) return;
  const not = (document.getElementById('gk-sonuc-not') as HTMLInputElement | null)?.value.trim() || undefined;
  const kopyaOnayi = (document.getElementById('gk-sonuc-kopya') as HTMLInputElement | null)?.checked === true;
  if (karar === 'yeniden-gonder' && !kopyaOnayi) {
    sonucPaneli = { ...p, kopyaOnayi: false, uyari: 'Yeniden gönderim için kopya riskini açıkça onaylamanız gerekiyor.' };
    renderGelenKutusu();
    return;
  }
  if (karar === 'yeniden-gonder'
    && !window.confirm('Bu mesaj karşı tarafa GİTMİŞ olabilir. Yeniden gönderim müşteriye aynı yanıtı ikinci kez ulaştırabilir. Devam edilsin mi?')) return;
  mesgul = true;
  const id = secili.id;
  const res = await apiFetch(`/api/v2/omni/giden/${encodeURIComponent(mesajId)}/sonuc`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ karar, not, kopyaRiskiKabul: karar === 'yeniden-gonder' ? true : undefined }),
  }).catch(() => null);
  const j = res ? await res.json().catch(() => ({})) as { ok?: boolean; error?: string; message?: string } : {};
  mesgul = false;
  if (res && res.ok && j.ok === true) {
    sonucPaneli = null;
    await loadKonusma(id, karar === 'teslim-edildi' ? 'Teslim kaydedildi (insan doğrulaması).'
      : karar === 'gonderilmedi' ? 'Satır kapatıldı: mesaj gitmemiş.'
        : 'Yeniden gönderim kuyruğa alındı — kopya riski kabul edildi.');
    return;
  }
  // Bayat ekran / eşzamanlı ikinci operatör: satır arada değişmişse karar YAZILMAZ, ekran tazelenir.
  const hata = j.error ?? '';
  if (hata === 'satir_arada_degisti' || hata === 'durum_uygun_degil') {
    sonucPaneli = null;
    await loadKonusma(id, 'Bu satır arada değişti (başka bir operatör ya da taşıyıcı işledi). Güncel durum yüklendi.');
    return;
  }
  const uyari = hata === 'kopya_riski_onayi_gerekli'
    ? (j.message ?? 'Kopya riski onayı gerekiyor.')
    : `Karar yazılamadı: ${j.message || hata || (res ? `HTTP ${res.status}` : 'bağlantı hatası')}`;
  sonucPaneli = { ...p, kopyaOnayi, uyari };
  renderGelenKutusu();
}

function bagla(root: HTMLElement): void {
  const btn = document.getElementById('gk-uygula');
  if (btn) {
    btn.addEventListener('click', async () => {
      const al = (id: string) => (document.getElementById(`gk-f-${id}`) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() || undefined;
      filtre = { channel: al('channel'), status: al('status'), tenantId: al('tenantId'), marka: al('marka'), etiket: al('etiket'), atama: al('atama'), sla: al('sla'), q: al('q') };
      secili = { kind: 'bos' };
      await loadGelenKutusu();
      renderGelenKutusu();
    });
  }
  // Kök her render'da korunur; dinleyici YALNIZ BİR KEZ bağlanır (çoklu istek üretmesin).
  if (root.dataset.gkBagli === '1') return;
  root.dataset.gkBagli = '1';
  root.addEventListener('click', (e) => {
    const hedef = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (!hedef || (hedef as HTMLButtonElement).disabled) return;
    const eylem = hedef.dataset.action;
    if (eylem === 'gk-ac' && hedef.dataset.konusmaId) {
      const id = hedef.dataset.konusmaId;
      secili = { kind: 'loading', id };
      renderGelenKutusu();
      void loadKonusma(id);
    } else if (eylem === 'gk-gonder') {
      void yanitGonder();
    } else if ((eylem === 'gk-onayla' || eylem === 'gk-reddet') && hedef.dataset.mesajId) {
      void oneriIslem(hedef.dataset.mesajId, eylem === 'gk-onayla' ? 'approve' : 'reject');
    } else if (eylem === 'gk-sonuc' && hedef.dataset.mesajId) {
      void sonucAc(hedef.dataset.mesajId);
    } else if (eylem === 'gk-sonuc-kapat') {
      sonucPaneli = null;
      renderGelenKutusu();
    } else if (eylem === 'gk-sonuc-ver' && hedef.dataset.mesajId && hedef.dataset.karar) {
      void sonucVer(hedef.dataset.mesajId, hedef.dataset.karar);
    } else if (eylem === 'gk-durum' || eylem === 'gk-ata' || eylem === 'gk-etiket-kaydet' || eylem === 'gk-not-ekle'
      || eylem === 'gk-hazir-ekle' || eylem === 'gk-hazir-guncelle' || eylem === 'gk-hazir-arsivle') {
      void yonetimIslem(eylem, hedef);
    } else if (eylem === 'gk-hazir-kullan') {
      hazirCevabiKullan();
    }
  });
}
